import CardHeader from "components/CardHeader";
import { Account } from "models/Account";
import { Expense } from "models/Expense";
import { Invoice } from "models/Invoice";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { Button, Card, CardBody } from "reactstrap";
import { getInvoices } from "store/actions/Billing";
import { getExpenses } from "store/actions/Expenses";
import { getAccounts } from "store/actions/Accounts";
import { AccountState } from "store/reducers/Accounts";
import { ExpenseState } from "store/reducers/Expenses";
import { InvoiceState } from "store/reducers/Invoices";
import { RootState } from "store/store";
import { GetRouterQuery } from "../../components/Utils/GetRouterQuery";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

type TabId = "invoices" | "expenses" | "tickets" | "accounts";

interface Props {
    accountState: AccountState;
    expenseState: ExpenseState;
    invoiceState: InvoiceState;
}

interface Actions {
    getAccounts: () => void;
    getExpenses: () => void;
    getInvoices: () => void;
}

const formatDate = (value: Date, locale: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(value);

const formatCurrency = (value: number, locale: string) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(value);

const initialTab = (): TabId => {
    const url = window.location.toString();
    if (url.includes("expenses")) return "expenses";
    if (url.includes("tickets")) return "tickets";
    if (url.includes("customers") || url.includes("accounts")) return "accounts";
    return "invoices";
};

const TenantTab: React.FC<Props & Actions> = ({
    accountState,
    expenseState,
    getAccounts,
    getExpenses,
    getInvoices,
    invoiceState,
}) => {
    const { intlLocale, t } = useOfficeTranslation();
    const { id } = GetRouterQuery<{ id: string }>();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabId>(initialTab);
    const [visible, setVisible] = useState(10);

    useEffect(() => {
        getAccounts();
        getExpenses();
        getInvoices();
    }, [getAccounts, getExpenses, getInvoices]);

    const invoices = useMemo(
        () => invoiceState.getInvoices()
            .filter(invoice => invoice.tenant === id)
            .sort((a, b) => b.GetCreatedDate().getTime() - a.GetCreatedDate().getTime()),
        [id, invoiceState],
    );
    const expenses = useMemo(
        () => expenseState.getExpenses()
            .filter(expense => expense.tenant === id)
            .sort((a, b) => b.GetDate().getTime() - a.GetDate().getTime()),
        [expenseState, id],
    );
    const tenantAccounts = useMemo(
        () => accountState.getAccounts()
            .filter(account => account.tenant === id)
            .sort((a, b) => a.legal_name.localeCompare(b.legal_name)),
        [accountState, id],
    );
    const accountNames = useMemo(
        () => new Map(accountState.getAccounts().map(account => [account.id, account.legal_name])),
        [accountState],
    );

    const tabs: Array<{ id: TabId; label: string; icon: string; count: number }> = [
        { id: "invoices", label: t("common.invoices"), icon: "mdi-file-document-outline", count: invoices.length },
        { id: "expenses", label: t("common.expenses"), icon: "mdi-cash-minus", count: expenses.length },
        { id: "tickets", label: t("common.tickets"), icon: "mdi-ticket-outline", count: 0 },
        { id: "accounts", label: t("common.accounts"), icon: "mdi-domain", count: tenantAccounts.length },
    ];
    const active = tabs.find(tab => tab.id === activeTab)!;
    const items = activeTab === "invoices"
        ? invoices
        : activeTab === "expenses"
            ? expenses
            : activeTab === "accounts"
                ? tenantAccounts
                : [];
    const remaining = Math.max(0, items.length - visible);

    const selectTab = (tab: TabId) => {
        setActiveTab(tab);
        setVisible(10);
    };

    const invoiceRow = (invoice: Invoice) => {
        const status = invoice.quote
            ? { label: t("common.quote"), className: "badge-soft-primary" }
            : invoice.draft
                ? { label: t("common.draft"), className: "badge-soft-secondary" }
                : invoice.IsPaid()
                    ? { label: t("common.paid"), className: "badge-soft-success" }
                    : invoice.IsPayWhenPaid()
                        ? { label: t("common.payWhenPaid"), className: "badge-soft-warning" }
                        : invoice.GetDeadlineDate().getTime() < Date.now()
                            ? { label: t("common.overdue"), className: "badge-soft-danger" }
                            : { label: t("common.open"), className: "badge-soft-warning" };
        return <div key={invoice.id} className="d-flex justify-content-between align-items-center gap-3 border-bottom py-3">
            <div className="min-w-0">
                <Link className="fw-medium d-block text-truncate" to={`/invoice/${invoice.id}`}>
                    {invoice.GetDisplayName() || t("tenants.tenantTab.untitledInvoice")}
                </Link>
                <small className="text-muted">
                    {accountNames.get(invoice.customer) ?? t("common.unknownAccount")} · {formatDate(invoice.GetCreatedDate(), intlLocale)}
                </small>
            </div>
            <div className="text-end flex-shrink-0">
                <span className="d-block fw-medium">{formatCurrency(invoice.CalcGrandTotal(), intlLocale)}</span>
                <span className={`badge ${status.className}`}>{status.label}</span>
            </div>
        </div>;
    };

    const expenseRow = (expense: Expense) =>
        <div key={expense.id} className="d-flex justify-content-between align-items-center gap-3 border-bottom py-3">
            <div className="min-w-0">
                <Link className="fw-medium d-block text-truncate" to={`/expenses/${expense.id}`}>
                    {expense.name || t("tenants.tenantTab.untitledExpense")}
                </Link>
                <small className="text-muted">
                    {accountState.getAccountById(expense.account_id)?.legal_name ?? t("common.unknownSupplier")} · {formatDate(expense.GetDate(), intlLocale)}
                </small>
            </div>
            <span className="fw-medium flex-shrink-0">{formatCurrency(expense.total, intlLocale)}</span>
        </div>;

    const accountRow = (account: Account) =>
        <div key={account.id} className="d-flex justify-content-between align-items-center gap-3 border-bottom py-3">
            <div className="min-w-0">
                <Link className="fw-medium d-block text-truncate" to={`/accounts/${account.id}`}>
                    {account.legal_name}
                </Link>
                <small className="text-muted">
                    {account.email || t("common.noEmail")} · {account.roles?.join(", ") || t("tenants.tenantTab.noRole")}
                </small>
            </div>
            <Link className="btn btn-light btn-sm flex-shrink-0" to={`/accounts/${account.id}`} aria-label={t("tenants.tenantTab.openAccount", {name: account.legal_name})}>
                <i className="mdi mdi-chevron-right" />
            </Link>
        </div>;

    return <Card>
        <CardBody>
            <CardHeader
                title={active.label}
                icon={active.icon}
                count={active.count}
                actions={activeTab === "expenses"
                    ? <Button color="primary" size="sm" onClick={() => navigate(`/tenants/${id}/expenses/add`)}>
                        <i className="mdi mdi-plus me-1" />{t("common.expense")}
                    </Button>
                    : undefined}
            />
            <div className="d-flex flex-wrap gap-2 py-3 border-bottom">
                {tabs.map(tab =>
                    <Button
                        key={tab.id}
                        color={activeTab === tab.id ? "primary" : "light"}
                        size="sm"
                        onClick={() => selectTab(tab.id)}
                    >
                        {tab.label}
                        <span className={`badge ms-2 ${activeTab === tab.id ? "bg-white text-primary" : "bg-primary bg-soft text-primary"}`}>
                            {tab.count}
                        </span>
                    </Button>
                )}
            </div>

            {activeTab === "tickets" && <p className="text-muted mt-3 mb-0">{t("tenants.tenantTab.noTicketsForThisTenant")}</p>}
            {activeTab !== "tickets" && items.length === 0 &&
                <p className="text-muted mt-3 mb-0">{t("tenants.tenantTab.noItemFound", { item: active.label.toLowerCase() })}</p>}
            {activeTab === "invoices" && invoices.slice(0, visible).map(invoiceRow)}
            {activeTab === "expenses" && expenses.slice(0, visible).map(expenseRow)}
            {activeTab === "accounts" && tenantAccounts.slice(0, visible).map(accountRow)}

            {remaining > 0 &&
                <Button color="light" className="w-100 mt-3" onClick={() => setVisible(items.length)}>
                    {t("tenants.tenantTab.showCountMoreItem", { count: remaining, item: active.label.toLowerCase() })}
                </Button>}
        </CardBody>
    </Card>;
};

const mapStateToProps = (state: RootState) => ({
    accountState: state.Accounts,
    expenseState: state.Expenses,
    invoiceState: state.Invoices,
});

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ getAccounts, getExpenses, getInvoices }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TenantTab);
